import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { differenceInDays, addDays } from "date-fns";
import { useEffect } from "react";

export type SubscriptionStatus = 'trial' | 'trial_pending_mandate' | 'active' | 'expiring_soon' | 'expired' | 'grace' | 'suspended';
export type SubscriptionPlanType = 'trial' | 'monthly' | 'half_yearly' | 'yearly' | 'three_year' | 'five_year';

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  planType: SubscriptionPlanType;
  selectedPlanForTrial: SubscriptionPlanType;
  expiresAt: Date | null;
  trialEndsAt: Date | null;
  graceEndsAt: Date | null;
  daysRemaining: number;
  canWrite: boolean;
  canAccessTools: boolean;
  isExpiringSoon: boolean;
  isInGrace: boolean;
  isSuspended: boolean;
  autoRenewEnabled: boolean;
  mandateStatus: string | null;
  isTrialPendingMandate: boolean;
  isTrialActive: boolean;
}

export interface SubscriptionPayment {
  id: string;
  tenant_id: string;
  amount: number;
  currency: string;
  plan_type: SubscriptionPlanType;
  payment_method: string | null;
  invoice_url: string | null;
  status: string;
  paid_at: string | null;
  invoice_number: string | null;
  created_at: string;
  razorpay_payment_id: string | null;
  razorpay_order_id: string | null;
}

// DEPRECATED: These constants are kept for backward compatibility only.
// Use useSubscriptionPlans() hook instead for real-time database-driven values.
// @deprecated - Use useSubscriptionPlans().planPricing instead
export const PLAN_PRICING = {
  trial: 0,
  monthly: 199,
  half_yearly: 799,
  yearly: 1299,
  three_year: 3897,
  five_year: 6495,
} as const;

// @deprecated - Use useSubscriptionPlans().planDurations instead
export const PLAN_DURATION_DAYS = {
  trial: 30,
  monthly: 30,
  half_yearly: 182,
  yearly: 365,
  three_year: 1095,
  five_year: 1825,
} as const;

// @deprecated - Use useSubscriptionPlans().planLabels instead
export const PLAN_LABELS = {
  trial: 'Trial',
  monthly: 'Monthly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
  three_year: '3 Year',
  five_year: '5 Year',
} as const;

export const GRACE_PERIOD_DAYS = 7;

function calculateSubscriptionStatus(tenant: any): SubscriptionInfo {
  const now = new Date();
  
  // Default values
  let status: SubscriptionStatus = 'trial';
  let expiresAt: Date | null = null;
  let trialEndsAt: Date | null = null;
  let graceEndsAt: Date | null = null;
  let daysRemaining = 0;
  
  const trialUsed = tenant?.trial_used ?? false;
  const planType: SubscriptionPlanType = tenant?.subscription_plan_enum || 'trial';
  const selectedPlanForTrial: SubscriptionPlanType = tenant?.selected_plan_for_trial || 'yearly';
  const autoRenewEnabled = tenant?.auto_renew_enabled ?? true; // Default ON
  const mandateStatus = tenant?.mandate_status || null;
  
  // Parse dates
  if (tenant?.trial_ends_at) {
    trialEndsAt = new Date(tenant.trial_ends_at);
  }
  if (tenant?.subscription_expires_at) {
    expiresAt = new Date(tenant.subscription_expires_at);
  }
  if (tenant?.grace_ends_at) {
    graceEndsAt = new Date(tenant.grace_ends_at);
  }
  
  // Check for trial pending mandate first
  if (!trialUsed && mandateStatus === 'pending') {
    status = 'trial_pending_mandate';
    daysRemaining = 30; // Full trial awaits
  } else if (!trialUsed && trialEndsAt) {
    // On trial (mandate authorized)
    if (now > trialEndsAt) {
      // Trial expired - no grace for trial users
      status = 'expired';
      daysRemaining = 0;
    } else if (differenceInDays(trialEndsAt, now) <= 7) {
      status = 'expiring_soon';
      daysRemaining = differenceInDays(trialEndsAt, now);
    } else {
      status = 'trial';
      daysRemaining = differenceInDays(trialEndsAt, now);
    }
  } else if (expiresAt) {
    // Paid subscription
    if (now > expiresAt) {
      // Subscription expired - check grace
      if (graceEndsAt && now <= graceEndsAt) {
        status = 'grace';
        daysRemaining = differenceInDays(graceEndsAt, now);
      } else if (graceEndsAt && now > graceEndsAt) {
        status = 'suspended';
        daysRemaining = 0;
      } else {
        status = 'expired';
        daysRemaining = 0;
      }
    } else if (differenceInDays(expiresAt, now) <= 7) {
      status = 'expiring_soon';
      daysRemaining = differenceInDays(expiresAt, now);
    } else {
      status = 'active';
      daysRemaining = differenceInDays(expiresAt, now);
    }
  }
  
  // Calculate permissions - trial_pending_mandate gets full access too
  const canWrite = ['trial', 'trial_pending_mandate', 'active', 'expiring_soon'].includes(status);
  const canAccessTools = canWrite;
  const isExpiringSoon = status === 'expiring_soon';
  const isInGrace = status === 'grace';
  const isSuspended = status === 'suspended';
  const isTrialPendingMandate = status === 'trial_pending_mandate';
  const isTrialActive = status === 'trial';
  
  return {
    status,
    planType,
    selectedPlanForTrial,
    expiresAt,
    trialEndsAt,
    graceEndsAt,
    daysRemaining,
    canWrite,
    canAccessTools,
    isExpiringSoon,
    isInGrace,
    isSuspended,
    autoRenewEnabled,
    mandateStatus,
    isTrialPendingMandate,
    isTrialActive,
  };
}

export function useSubscription() {
  const { tenant, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  
  const { data: subscriptionInfo, isLoading } = useQuery({
    queryKey: ['subscription', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return null;
      
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          subscription_status_enum,
          subscription_plan_enum,
          subscription_expires_at,
          trial_started_at,
          trial_ends_at,
          trial_used,
          grace_started_at,
          grace_ends_at,
          suspended_at,
          auto_renew_enabled,
          last_payment_at,
          next_billing_at,
          mandate_id,
          mandate_status,
          mandate_created_at,
          mandate_authorized_at,
          selected_plan_for_trial
        `)
        .eq('id', tenant.id)
        .single();
      
      if (error) throw error;
      return calculateSubscriptionStatus(data);
    },
    enabled: !!tenant?.id,
    staleTime: 1000 * 60, // 1 minute
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
  
  // ✅ REALTIME LISTENER for instant UI updates after payment
  useEffect(() => {
    if (!tenant?.id) return;
    
    // Subscribe to tenant changes (subscription updates)
    const tenantChannel = supabase
      .channel(`tenant-subscription-${tenant.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenants',
          filter: `id=eq.${tenant.id}`
        },
        (payload) => {
          console.log('🔄 Tenant subscription updated in realtime:', payload);
          // Immediately invalidate and refetch for instant sync
          queryClient.invalidateQueries({ queryKey: ['subscription', tenant.id] });
          queryClient.refetchQueries({ queryKey: ['subscription', tenant.id] });
        }
      )
      .subscribe();
    
    // Subscribe to payment success events
    const paymentChannel = supabase
      .channel(`subscription-payments-${tenant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscription_payments',
          filter: `tenant_id=eq.${tenant.id}`
        },
        (payload) => {
          console.log('💰 Payment record changed in realtime:', payload);
          // Invalidate payment history
          queryClient.invalidateQueries({ queryKey: ['subscription-payments', tenant.id] });
          queryClient.refetchQueries({ queryKey: ['subscription-payments', tenant.id] });
          // Also refresh subscription status as payment affects it
          queryClient.invalidateQueries({ queryKey: ['subscription', tenant.id] });
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(tenantChannel);
      supabase.removeChannel(paymentChannel);
    };
  }, [tenant?.id, queryClient]);
  
  // Fetch payment history
  const { data: payments } = useQuery({
    queryKey: ['subscription-payments', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from('subscription_payments')
        .select('*')
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SubscriptionPayment[];
    },
    enabled: !!tenant?.id,
  });
  
  // Toggle auto-renew
  const toggleAutoRenew = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!tenant?.id) throw new Error('No tenant');
      
      const { error } = await supabase
        .from('tenants')
        .update({ auto_renew_enabled: enabled })
        .eq('id', tenant.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', tenant?.id] });
    },
  });
  
  return {
    subscription: subscriptionInfo,
    payments: payments || [],
    isLoading: authLoading || isLoading,
    toggleAutoRenew,
    // Convenience accessors
    canWrite: subscriptionInfo?.canWrite ?? true,
    canAccessTools: subscriptionInfo?.canAccessTools ?? true,
    isExpired: subscriptionInfo?.status === 'expired',
    isInGrace: subscriptionInfo?.isInGrace ?? false,
    isSuspended: subscriptionInfo?.isSuspended ?? false,
    isExpiringSoon: subscriptionInfo?.isExpiringSoon ?? false,
    daysRemaining: subscriptionInfo?.daysRemaining ?? 0,
    status: subscriptionInfo?.status ?? 'trial',
  };
}
